import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { DRIFT_NOTICE_MINUTES, Permission, PunchStatus, PunchType } from '@construction/shared';
import { attendanceApi, type Punch } from '@/api/endpoints';
import { Badge, Button } from '@/components/ui';
import { useErrorMessage } from '@/hooks/useErrorMessage';
import { cn } from '@/lib/cn';
import { formatClock } from '@/lib/format';
import { usePermissions } from '@/lib/permissions';
import { PUNCH_OVERRIDE_KEY, PUNCH_REASON_KEY } from '@/lib/punch-labels';
import { invalidateAttendance } from './invalidate';

/** 状态 → 文案。显式映射，不靠字符串拼接猜 key。 */
const STATUS_KEY: Record<string, string> = {
  APPROVED: 'punch.statusApproved',
  PENDING_REVIEW: 'punch.statusPending',
  REJECTED: 'punch.statusRejected',
};

/** 来源 → 文案 */
const SOURCE_KEY: Record<string, string> = {
  ONLINE: 'attendance.sourceOnline',
  OFFLINE_SYNC: 'attendance.sourceOffline',
  MANUAL_ADMIN: 'attendance.sourceManual',
};

function statusTone(status: string): 'ok' | 'danger' | 'warn' {
  if (status === PunchStatus.APPROVED) return 'ok';
  if (status === PunchStatus.REJECTED) return 'danger';
  return 'warn';
}

/** 通过 / 拒绝。桌面表格行和手机卡片共用一份 */
export function usePunchReview(punch: Punch) {
  const queryClient = useQueryClient();
  const toMessage = useErrorMessage();
  const [error, setError] = useState<string | null>(null);

  const review = useMutation({
    mutationFn: (status: PunchStatus) => attendanceApi.review(punch.id, status),
    onSuccess: () => {
      // 审掉一条待复核：那个人的工时、待复核页签、异常（通过后参与配对）、概览都会动（CR-052）
      invalidateAttendance(queryClient);
    },
    onError: (err) => setError(toMessage(err)),
  });

  return {
    review,
    error,
    needsReview: punch.status === PunchStatus.PENDING_REVIEW,
    // 只看考勤的人看得到待复核，但没有通过 / 拒绝按钮（CR-040）
    canReview: usePermissions().can(Permission.ATTENDANCE_MANAGE),
  };
}

/**
 * 打卡时刻和服务端收到的时刻差多久。
 *
 * **只对实时上传的打卡有意义**：离线补传和管理员补录本来就是事后才到的，
 * 差几个钟头是正常的。改之前不分来源都显示，于是每一条补传记录后面都挂着
 * 「+4989 分钟」这种数字，看着像满屏异常，真正的时钟问题反而淹没在里面。
 */
function driftMinutesOf(punch: Punch): number {
  if (punch.source !== 'ONLINE') return 0;
  return Math.round((new Date(punch.receivedAt).getTime() - new Date(punch.occurredAt).getTime()) / 60_000);
}

function PunchTime({ punch, timezone }: { punch: Punch; timezone: string }) {
  const { t, i18n } = useTranslation();
  const drift = driftMinutesOf(punch);
  return (
    <>
      {formatClock(punch.occurredAt, timezone, i18n.language)}
      {/* 阈值和日报 / 隐蔽工程共用一个常量，同一份数据不会一页标一页不标（CR-033） */}
      {drift > DRIFT_NOTICE_MINUTES && (
        <span className="ml-1 text-safety-600">
          (+{drift}
          {t('attendance.minutesLate')})
        </span>
      )}
    </>
  );
}

function PunchDistance({ punch }: { punch: Punch }) {
  const { t } = useTranslation();
  // 没有坐标时显示「无定位」，不能写 0 —— 那会看成「站在工地正中央」
  if (punch.distanceM == null) return <span className="text-ink-3">{t('attendance.noLocation')}</span>;
  return (
    <>
      {Math.round(punch.distanceM)} m
      {punch.accuracyM != null && <span className="ml-1 text-caption text-ink-3">±{Math.round(punch.accuracyM)}</span>}
    </>
  );
}

/**
 * 「原因」一格（CR-047）：系统判定 + 工人选的理由 + 工人自己写的说明 + 补录 / 复核的说明。
 *
 * 客户原话：「考勤内 原因里看不到说明内容。只显示 无定位工人已说明」。
 * 改之前这一格只翻译了系统判定码 —— 「工人已说明」却看不到说明了什么，
 * 而管理员决定通过还是拒绝，要看的恰恰是这一句。接口一直带着这些字段，只是没显示。
 * 工人自写的内容原样显示，不翻译（CLAUDE.md：用户输入不翻译）。
 */
export function PunchReason({ punch }: { punch: Punch }) {
  const { t } = useTranslation();
  const override = punch.overrideReason
    ? PUNCH_OVERRIDE_KEY[punch.overrideReason]
      ? t(PUNCH_OVERRIDE_KEY[punch.overrideReason]!)
      : punch.overrideReason
    : null;
  const workerNote = punch.overrideReasonNote?.trim() || null;
  const reviewNote = punch.reviewNote?.trim() || null;

  const lines = [
    punch.reason
      ? { key: 'verdict', tone: 'text-ink-2', text: t(PUNCH_REASON_KEY[punch.reason] ?? 'attendance.reasonUnknown') }
      : null,
    override ? { key: 'override', tone: 'font-medium text-ink', text: override } : null,
    workerNote ? { key: 'note', tone: 'text-ink', text: t('attendance.workerNote', { note: workerNote }) } : null,
    reviewNote
      ? {
          key: 'review',
          tone: 'text-ink-2',
          text: t(punch.source === 'MANUAL_ADMIN' ? 'attendance.manualNote' : 'attendance.reviewNote', {
            note: reviewNote,
          }),
        }
      : null,
  ].filter((line): line is { key: string; tone: string; text: string } => line !== null);

  if (lines.length === 0) return <span className="text-ink-3">—</span>;
  return (
    <span className="flex min-w-0 flex-col gap-0.5 break-words">
      {lines.map((line) => (
        <span key={line.key} className={line.tone}>
          {line.text}
        </span>
      ))}
    </span>
  );
}

/**
 * 一条打卡记录：桌面表格里的一行。
 *
 * **不在这里定列宽**：列宽由 `PersonDetail` 那张表的 `<colgroup>` 统一给
 * （CR-031 —— 之前每天各一张表、各自算宽度，跨天看过去是错开的）。
 * 这里只管每一列的对齐方式：数字列右对齐 + `tabular-nums`。
 * 「原因」那一列允许换行 —— 工人的说明是给管理员读的，截断就等于没显示（CR-047）。
 */
export function PunchRow({
  punch,
  timezone,
  lead,
}: {
  punch: Punch;
  timezone: string;
  /** 前面多一列：不按日期的「待复核」页签里放人名和日期（CR-052） */
  lead?: ReactNode;
}) {
  const { t } = useTranslation();
  const { review, error, needsReview, canReview } = usePunchReview(punch);

  return (
    <tr className={cn('border-b border-rule align-top last:border-0', needsReview && 'bg-safety-50/40')}>
      {lead !== undefined && <td className="px-3 py-2">{lead}</td>}
      <td className="px-3 py-2 font-mono text-caption tabular-nums">
        <PunchTime punch={punch} timezone={timezone} />
      </td>
      <td className="whitespace-nowrap px-3 py-2">{punch.type === PunchType.IN ? t('punch.clockIn') : t('punch.clockOut')}</td>
      <td className="px-3 py-2 text-right font-mono text-caption tabular-nums">
        <PunchDistance punch={punch} />
      </td>
      <td className="px-3 py-2 text-caption text-ink-2">
        {t(SOURCE_KEY[punch.source] ?? 'attendance.sourceOnline')}
        {punch.isMocked && <span className="ml-1.5 text-caption font-medium text-danger">{t('attendance.reasonMockLocation')}</span>}
      </td>
      <td className="px-3 py-2 text-caption">
        <PunchReason punch={punch} />
        {error && <div className="text-danger">{error}</div>}
      </td>
      <td className="px-3 py-2">
        <Badge tone={statusTone(punch.status)}>{t(STATUS_KEY[punch.status] ?? 'punch.statusApproved')}</Badge>
      </td>
      <td className="px-3 py-2 text-right">
        {needsReview && canReview && (
          // 右对齐、主动作在最右（按钮位置规矩）
          <div className="flex justify-end gap-2">
            <Button variant="secondary" loading={review.isPending} onClick={() => review.mutate(PunchStatus.REJECTED)}>
              {t('attendance.reject')}
            </Button>
            <Button loading={review.isPending} onClick={() => review.mutate(PunchStatus.APPROVED)}>
              {t('attendance.approve')}
            </Button>
          </div>
        )}
      </td>
    </tr>
  );
}

/**
 * 一条打卡记录：手机卡片（CR-047）。
 *
 * 客户原话：「app端里考勤排版没有像网页端一样有样式对齐」。改之前手机上也是那张 720px 的表，
 * 要左右滑才看得到原因和按钮。现在用一套定死列宽的 grid：时刻一列、内容一列、状态一列，
 * 每条打卡的时刻和状态上下对齐；原因在第二行，和内容列对齐。通过 / 拒绝戴手套能点（56px）。
 */
export function PunchItem({
  punch,
  timezone,
  lead,
}: {
  punch: Punch;
  timezone: string;
  /** 卡片最上面一行：不按日期的「待复核」页签里放人名和日期（CR-052） */
  lead?: ReactNode;
}) {
  const { t } = useTranslation();
  const { review, error, needsReview, canReview } = usePunchReview(punch);

  return (
    <li className={cn('border-b border-rule px-4 py-3 last:border-0', needsReview && 'bg-safety-50/40')}>
      {lead !== undefined && <div className="mb-1.5">{lead}</div>}
      <div className="grid grid-cols-[3.5rem_minmax(0,1fr)_auto] items-baseline gap-x-3 gap-y-1.5">
        <span className="font-mono text-body tabular-nums">
          <PunchTime punch={punch} timezone={timezone} />
        </span>
        <span className="flex min-w-0 flex-wrap items-baseline gap-x-3 text-body">
          <span className="whitespace-nowrap font-medium">{punch.type === PunchType.IN ? t('punch.clockIn') : t('punch.clockOut')}</span>
          <span className="font-mono text-caption tabular-nums">
            <PunchDistance punch={punch} />
          </span>
          <span className="text-caption text-ink-3">
            {t(SOURCE_KEY[punch.source] ?? 'attendance.sourceOnline')}
            {punch.isMocked && <span className="ml-1.5 text-caption font-medium text-danger">{t('attendance.reasonMockLocation')}</span>}
          </span>
        </span>
        <Badge tone={statusTone(punch.status)}>{t(STATUS_KEY[punch.status] ?? 'punch.statusApproved')}</Badge>

        <span className="col-span-2 col-start-2 text-caption">
          <PunchReason punch={punch} />
        </span>
      </div>

      {error && <p className="mt-2 text-caption text-danger">{error}</p>}

      {needsReview && canReview && (
        <div className="mt-2 grid grid-cols-2 gap-2">
          {/* 左拒绝右通过，主动作在右（按钮位置规矩） */}
          <Button
            variant="secondary"
            loading={review.isPending}
            onClick={() => review.mutate(PunchStatus.REJECTED)}
          >
            {t('attendance.reject')}
          </Button>
          <Button loading={review.isPending} onClick={() => review.mutate(PunchStatus.APPROVED)}>
            {t('attendance.approve')}
          </Button>
        </div>
      )}
    </li>
  );
}
